/**
 * Stats Validation Utilities
 * 
 * Provides validation and clamping functions for player stat values.
 * Ensures all stat values remain within acceptable bounds as defined
 * in the game room requirements.
 */

/**
 * Validation bounds for each stat type
 */
export const StatsValidation = {
  health: { min: 0, max: 999 },
  will: { min: 0, max: 999 },
  attackBonus: { min: -99, max: 99 },
  defenseBonus: { min: -99, max: 99 },
  speed: { min: 0, max: 99 },
} as const;

export type StatName = keyof typeof StatsValidation;

/**
 * Validates that a stat value is within acceptable bounds.
 * 
 * @param statName - The name of the stat to validate
 * @param value - The value to validate
 * @returns true if the value is within bounds, false otherwise
 */
export function validateStatValue(statName: StatName, value: number): boolean {
  const bounds = StatsValidation[statName];
  if (!bounds) {
    return false;
  }
  return value >= bounds.min && value <= bounds.max;
}

/**
 * Clamps a stat value to be within acceptable bounds.
 * 
 * @param statName - The name of the stat to clamp
 * @param value - The value to clamp
 * @returns The clamped value within the stat's bounds
 */
export function clampStatValue(statName: StatName, value: number): number {
  const bounds = StatsValidation[statName];
  if (!bounds) {
    return value;
  }
  return Math.max(bounds.min, Math.min(bounds.max, value));
}

/**
 * Validates all stats in a stats update object.
 * 
 * @param updates - Object containing stat updates
 * @returns Object with validation results for each stat
 */
export function validateStatsUpdate(updates: Partial<Record<StatName, number>>): {
  isValid: boolean;
  errors: Partial<Record<StatName, string>>;
} {
  const errors: Partial<Record<StatName, string>> = {};
  
  for (const [statName, value] of Object.entries(updates)) {
    if (value !== undefined && !validateStatValue(statName as StatName, value)) {
      const bounds = StatsValidation[statName as StatName];
      errors[statName as StatName] = `${statName} must be between ${bounds.min} and ${bounds.max}`;
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}
